import type { IResponse } from "../app/@types/ApiResponse";
import { toast } from "sonner";
import type { ExternalToast } from "sonner";

// Type definitions for toast messages
type ToastOptions = ExternalToast & {
    closeButton?: boolean;
};

type ResponseHandlerProps = {
    response?: IResponse<any>;
    fallbackMessage?: string;
}

type SimpleToastProps = {
    message: string;
    variant?: "success" | "error" | "info" | "warning" | "default";
    title?: string;
    duration?: number;
    description?: string;
    closeButton?: boolean;
}

type CustomToastProps = {
    title?: string;
    description?: string;
    variant?: "success" | "error" | "info" | "warning" | "default";
    duration?: number;
    closeButton?: boolean;
    action?: {
        label: string;
        onClick: () => void;
    };
}

/**
 * Handle API response and show appropriate toast message
 * @param response - API response object
 * @param fallbackMessage - Message to show if response message is not available
 */
export const toastResponseHandler = ({ response, fallbackMessage }: ResponseHandlerProps) => {
    const description = response?.message ? response.message : fallbackMessage;
    
    const options: ToastOptions = {
        closeButton: true,
    };

    switch (response?.status) {
        case "Success":
            toast.success(description || "Successfully completed the action.", {closeButton:true});
            break;
        case "Fail":
            toast.error(description || "Unable to perform action. Please try again.", options);
            break;
        default:
            toast.info(description || "An unexpected error occurred.", options);
            break;
    }
};

/**
 * Show a simple toast message with minimal configuration
 * @param message - The message to display
 * @param variant - Toast type (success, error, info, warning, default)
 * @param title - Optional title
 * @param duration - Optional duration in milliseconds
 * @param description - Optional additional description
 * @param closeButton - Show close button (default: true)
 */
export const showToast = ({ 
    message, 
    variant = "default", 
    title,
    duration,
    description,
    closeButton = true
}: SimpleToastProps) => {
    const options: ToastOptions = {
        description: description || message,
        duration,
        closeButton,
    };

    switch (variant) {
        case "success":
            toast.success(title || message, options);
            break;
        case "error":
            toast.error(title || message, options);
            break;
        case "info":
            toast.info(title || message, options);
            break;
        case "warning":
            toast.warning(title || message, options);
            break;
        default:
            toast(title || message, options);
            break;
    }
};

/**
 * Show a success toast message
 * @param message - Success message to display
 * @param title - Optional title
 */
export const showSuccessToast = (message: string, title?: string) => {
    toast.success(title || message, {
        description: title ? message : undefined,
        closeButton: true,
    });
};

/**
 * Show an error toast message
 * @param message - Error message to display
 * @param title - Optional title
 */
export const showErrorToast = (message: string, title?: string) => {
    toast.error(title || message, {
        description: title ? message : undefined,
        closeButton: true,
    });
};

/**
 * Show an info toast message
 * @param message - Info message to display
 * @param title - Optional title
 */
export const showInfoToast = (message: string, title?: string) => {
    toast.info(title || message, {
        description: title ? message : undefined,
        closeButton: true,
    });
};

/**
 * Show a warning toast message
 * @param message - Warning message to display
 * @param title - Optional title
 */
export const showWarningToast = (message: string, title?: string) => {
    toast.warning(title || message, {
        description: title ? message : undefined,
        closeButton: true,
    });
};

/**
 * Show a custom toast with full control over all options
 * @param props - Custom toast configuration
 */
export const showCustomToast = ({
    title,
    description,
    variant = "default",
    duration,
    closeButton = true,
    action,
}: CustomToastProps) => {
    const options: ToastOptions = {
        description,
        duration,
        closeButton,
        action,
    };

    switch (variant) {
        case "success":
            toast.success(title || description || "", options);
            break;
        case "error":
            toast.error(title || description || "", options);
            break;
        case "info":
            toast.info(title || description || "", options);
            break;
        case "warning":
            toast.warning(title || description || "", options);
            break;
        default:
            toast(title || description || "", options);
            break;
    }
};

/**
 * Show a loading toast
 * @param message - Loading message to display
 */
export const showLoadingToast = (message: string = "Loading...") => {
    return toast.loading(message, {
        closeButton: true,
    });
};

/**
 * Show a promise toast that updates based on promise status
 * @param promise - Promise to track
 * @param messages - Messages for loading, success, and error states
 */
export const showPromiseToast = <T,>(
    promise: Promise<T>,
    messages: {
        loading: string;
        success: string | ((data: T) => string);
        error: string | ((error: any) => string);
    }
) => {
    return toast.promise(promise, {
        loading: messages.loading,
        success: messages.success,
        error: messages.error,
    });
};

export const handleAsyncToast = async <T>(
  operation: () => Promise<T>,
  options: {
    loadingMessage?: string;
    successMessage?: string;
    errorMessage?: string;
  } = {}
): Promise<T> => {
  const {
    // loadingMessage = 'Processing...',
    successMessage = 'Operation completed successfully',
    errorMessage = 'Operation failed. Please try again.'
  } = options;

  // const loadingToast = showLoadingToast(loadingMessage);

  try {
    const result = await operation();
    // dismissToast(loadingToast);
    // showSuccessToast(result, successMessage);
    toastResponseHandler({ response: result as any, fallbackMessage: successMessage });
    // showSuccessToast(successMessage);
    return result;
  } catch (error) {
    // dismissToast(loadingToast);
    showErrorToast(errorMessage);
    toastResponseHandler({ response: error as any, fallbackMessage: errorMessage });
    throw error;
  }
};